import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Sparkles, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { CustomFieldRow, V2AttributeOption } from '../api/migration-v1-v2';

interface Props {
  open: boolean;
  schoolId: number;
  schoolName?: string;
  /** Code du lot (parents.fathers, parents.mothers, students.eleves). */
  lotCode: string;
  /** Libellé du lot affiché dans le titre. */
  lotLabel: string;
  onClose: () => void;
  /** Appelé après un save réussi pour rafraîchir la cellule MAPPING. */
  onSaved?: () => void;
}

/**
 * Catalogue des `AttributeType` V2 disponibles pour CRÉER un nouveau champ perso.
 * Sous-ensemble de `AttributeTypeEnum` côté V2, synchronisé avec
 * `CustomFieldMappingAiPremapper::ATTRIBUTE_TYPES` côté backend. Composites
 * (ADDRESS/PHONE) exclus (migrés par étapes natives dédiées).
 */
const V2_ATTRIBUTE_TYPES: Array<{ code: string; label: string }> = [
  { code: 'STRING', label: 'Texte court' },
  { code: 'TEXT_AREA', label: 'Texte long' },
  { code: 'RICH_TEXT', label: 'Texte enrichi' },
  { code: 'INTEGER', label: 'Nombre entier' },
  { code: 'FLOAT', label: 'Nombre décimal' },
  { code: 'CURRENCY', label: 'Montant' },
  { code: 'PERCENTAGE', label: 'Pourcentage' },
  { code: 'BOOLEAN', label: 'Oui / Non' },
  { code: 'EMAIL', label: 'Email' },
  { code: 'URL', label: 'URL' },
  { code: 'DATE', label: 'Date' },
  { code: 'DATETIME', label: 'Date et heure' },
  { code: 'TIME', label: 'Heure' },
  { code: 'POSTAL_CODE', label: 'Code postal' },
  { code: 'IBAN', label: 'IBAN' },
  { code: 'COLOR', label: 'Couleur' },
  { code: 'SELECT', label: 'Choix unique' },
  { code: 'MULTI_SELECT', label: 'Choix multiple' },
  { code: 'RADIO', label: 'Boutons radio' },
  { code: 'CHECKBOX', label: 'Cases à cocher' },
  { code: 'FILE', label: 'Fichier' },
  { code: 'IMAGE', label: 'Image' },
];

/** Mapping suggéré V1 → V2 (utilisé par « Suggestion automatique »). */
const V1_TYPE_TO_V2_SUGGEST: Record<string, string> = {
  textinput: 'STRING',
  textarea: 'TEXT_AREA',
  number: 'INTEGER',
  email: 'EMAIL',
  date: 'DATE',
  select: 'SELECT',
  radio: 'RADIO',
  checkbox: 'MULTI_SELECT',
  file: 'FILE',
};

function lotCodeToForm(lotCode: string): 1 | 2 | null {
  if (lotCode === 'students.eleves') return 1;
  if (lotCode === 'parents.fathers' || lotCode === 'parents.mothers') return 2;
  return null;
}

const NONE_VALUE = '__none__';

/** Normalisation de nom (alignée backend) pour détecter la réutilisation auto. */
function normalizeName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

interface EditState {
  /** Attribut V2 existant choisi (null = créer par type). */
  targetId: string | null;
  /** Type V2 (create) OU type de l'attribut existant choisi. */
  targetCode: string | null;
  dirty: boolean;
  /** Provenance : 'ai' si pré-rempli par Gemini (icône ✨). */
  source?: 'ai';
  confidence?: number | null;
}

/**
 * Drawer de mapping des champs personnalisés V1 (`customfields`) vers V2.
 *
 * Deux stratégies par champ :
 *  - **Réutiliser un champ V2 existant** (l'attribut est déjà dans le tenant) →
 *    on stocke `targetId` : le migrateur écrit la valeur sur cet attribut.
 *  - **Créer un nouveau champ** en choisissant un `AttributeType` V2 → `targetId`
 *    null : le migrateur réutilise par nom si un attribut correspond, sinon crée.
 *
 * Le drawer affiche, par champ, ce qui va se passer (existant / réutilisé auto /
 * à créer), à partir des attributs V2 réellement présents dans le tenant.
 *
 * Persistance : `PUT /api/migration/config/mappings/custom_field?schoolId=X`.
 */
export function CustomFieldsMappingDialog({
  open,
  schoolId,
  schoolName,
  lotCode,
  lotLabel,
  onClose,
  onSaved,
}: Props) {
  const form = lotCodeToForm(lotCode);
  const [rows, setRows] = useState<CustomFieldRow[]>([]);
  const [v2Attributes, setV2Attributes] = useState<V2AttributeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [savingAll, setSavingAll] = useState(false);
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiCount, setAiCount] = useState(0);

  const v2ById = useMemo(() => {
    const m = new Map<string, V2AttributeOption>();
    for (const a of v2Attributes) m.set(a.id, a);
    return m;
  }, [v2Attributes]);

  const v2ByName = useMemo(() => {
    const m = new Map<string, V2AttributeOption>();
    for (const a of v2Attributes) m.set(normalizeName(a.name), a);
    return m;
  }, [v2Attributes]);

  const runAiSuggest = async (unmappedIds: Set<string>) => {
    if (!form || unmappedIds.size === 0) {
      setAiCount(0);
      return;
    }
    setAiRunning(true);
    try {
      const resp = await migrationV1V2Api.aiSuggestCustomFields(schoolId, form);
      setAiEnabled(resp.enabled);
      if (!resp.enabled) return;
      let count = 0;
      setEdits((prev) => {
        const next = { ...prev };
        for (const s of resp.suggestions) {
          if (!unmappedIds.has(s.sourceId) || !s.targetCode) continue;
          next[s.sourceId] = {
            targetId: null,
            targetCode: s.targetCode,
            dirty: true,
            source: 'ai',
            confidence: s.confidence,
          };
          count++;
        }
        return next;
      });
      setAiCount(count);
    } catch {
      // Silencieux : l'IA est un confort, l'opérateur peut mapper à la main.
    } finally {
      setAiRunning(false);
    }
  };

  const load = async () => {
    if (!form) return;
    setLoading(true);
    setError(null);
    setAiCount(0);
    try {
      // Vue enrichie : champs V1 + attributs V2 existants + verdict.
      const res = await migrationV1V2Api.getCustomFieldsV2Preview(schoolId, form);
      setRows(res.fields);
      setV2Attributes(res.v2Attributes);
      setEdits({});
      if (aiEnabled !== false) {
        const unmapped = new Set(
          res.fields.filter((f) => !f.targetCode && !f.targetId).map((f) => f.sourceId)
        );
        if (unmapped.size > 0) void runAiSuggest(unmapped);
      }
    } catch (e: unknown) {
      // Repli : sans tenant V2 déployé, on charge la vue simple (types seuls).
      try {
        const res = await migrationV1V2Api.getCustomFields(schoolId, form);
        setRows(res.fields);
        setV2Attributes([]);
        setEdits({});
      } catch (e2: unknown) {
        setError(e2 instanceof Error ? e2.message : String(e2));
      }
      if (e instanceof Error && !error) {
        // On garde l'info mais sans bloquer : le repli a pu réussir.
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schoolId, lotCode]);

  /** Cible effective d'une ligne (édition prioritaire sur l'état chargé). */
  const effective = (r: CustomFieldRow): { targetId: string | null; targetCode: string | null } => {
    const e = edits[r.sourceId];
    if (e) return { targetId: e.targetId, targetCode: e.targetCode };
    return { targetId: r.targetId ?? null, targetCode: r.targetCode ?? null };
  };

  const isMapped = (r: CustomFieldRow): boolean => {
    const { targetId, targetCode } = effective(r);
    return !!targetId || !!targetCode;
  };

  const mappedCount = useMemo(() => rows.filter(isMapped).length, [rows, edits]);
  const dirtyRows = useMemo(() => rows.filter((r) => edits[r.sourceId]?.dirty), [rows, edits]);

  /** Valeur du <Select> pour une ligne : attr:{id} | type:{code} | none. */
  const selectValue = (r: CustomFieldRow): string => {
    const { targetId, targetCode } = effective(r);
    if (targetId) return `attr:${targetId}`;
    if (targetCode) return `type:${targetCode}`;
    return NONE_VALUE;
  };

  const onSelect = (r: CustomFieldRow, value: string) => {
    setEdits((prev) => {
      let next: EditState;
      if (value === NONE_VALUE) {
        next = { targetId: null, targetCode: null, dirty: true };
      } else if (value.startsWith('attr:')) {
        const id = value.slice(5);
        const attr = v2ById.get(id);
        next = { targetId: id, targetCode: attr?.type ?? 'STRING', dirty: true };
      } else {
        next = { targetId: null, targetCode: value.slice(5), dirty: true };
      }
      return { ...prev, [r.sourceId]: next };
    });
  };

  const handleAiRerun = () => {
    const unmapped = new Set(rows.filter((r) => !isMapped(r)).map((r) => r.sourceId));
    void runAiSuggest(unmapped);
  };

  /** Pré-remplit les lignes non mappées avec la suggestion de TYPE V1→V2. */
  const applySuggestions = () => {
    setEdits((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        if (isMapped(r)) continue;
        const suggest = V1_TYPE_TO_V2_SUGGEST[r.sourceType.toLowerCase()];
        if (suggest) next[r.sourceId] = { targetId: null, targetCode: suggest, dirty: true };
      }
      return next;
    });
  };

  const typeLabelFor = (code: string | null): string | null =>
    code ? V2_ATTRIBUTE_TYPES.find((t) => t.code === code)?.label ?? code : null;

  /** Verdict d'affichage : existant explicite / réutilisé auto par nom / à créer. */
  const verdictFor = (
    r: CustomFieldRow
  ): { kind: 'existing' | 'reuse' | 'create' | 'none'; name?: string } => {
    const { targetId, targetCode } = effective(r);
    if (targetId) {
      return { kind: 'existing', name: v2ById.get(targetId)?.name };
    }
    if (!targetCode) return { kind: 'none' };
    const match = v2ByName.get(normalizeName(r.sourceLabel));
    return match ? { kind: 'reuse', name: match.name } : { kind: 'create' };
  };

  const saveAll = async () => {
    if (dirtyRows.length === 0) return;
    setSavingAll(true);
    setError(null);
    try {
      const payload = rows.map((r) => {
        const { targetId, targetCode } = effective(r);
        const targetLabel = targetId
          ? v2ById.get(targetId)?.name ?? null
          : typeLabelFor(targetCode);
        return {
          type: 'custom_field',
          sourceId: r.sourceId,
          sourceLabel: r.sourceLabel,
          targetCode,
          targetLabel,
          targetId,
        };
      });
      await migrationV1V2Api.saveMappings('custom_field', payload, schoolId);
      onSaved?.();
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingAll(false);
    }
  };

  const willCreate = useMemo(
    () => rows.filter((r) => verdictFor(r).kind === 'create').length,
    [rows, edits, v2Attributes]
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:w-[min(95vw,1150px)] sm:max-w-[1150px]">
        <SheetHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col space-y-1">
              <SheetTitle>
                Champs personnalisés — {lotLabel}
                {schoolName ? ` — ${schoolName}` : ''}
              </SheetTitle>
              <SheetDescription>
                Pour chaque champ V1 : <strong>réutiliser</strong> un champ V2
                existant ou <strong>créer</strong> un nouveau champ (type). Le lot
                est bloqué tant que tous les champs ne sont pas traités.
              </SheetDescription>
            </div>
            {rows.length > 0 && (
              <div className="flex shrink-0 flex-col items-end gap-1">
                <Badge variant="secondary">
                  {mappedCount} / {rows.length}
                </Badge>
                {v2Attributes.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {willCreate} à créer · {rows.length - willCreate} réutilisés
                  </span>
                )}
              </div>
            )}
          </div>
        </SheetHeader>

        <SheetBody>
          {!form && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-800">
              Lot inconnu : <code>{lotCode}</code>
            </div>
          )}

          {form && loading && rows.length === 0 && (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Chargement…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800 mb-3">
              {error}
            </div>
          )}

          {form && !loading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600">
                    {rows.length} champs personnalisés V1 actifs.
                    {v2Attributes.length > 0
                      ? ` ${v2Attributes.length} champs V2 existants proposés.`
                      : ' (tenant V2 non déployé : réutilisation indisponible)'}
                  </p>
                  {aiCount > 0 && (
                    <p className="flex items-center gap-1.5 text-sm text-violet-700">
                      <Sparkles className="h-4 w-4 text-violet-500" />
                      {aiCount} suggestion{aiCount > 1 ? 's' : ''} IA (✨) — vérifiez
                      puis enregistrez.
                    </p>
                  )}
                </div>
                {aiEnabled === false ? (
                  <Button variant="outline" size="sm" onClick={applySuggestions} disabled={savingAll}>
                    <Wand2 className="h-4 w-4 mr-1" />
                    Suggestion auto
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAiRerun}
                    disabled={savingAll || aiRunning}
                  >
                    {aiRunning ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-1 text-violet-500" />
                    )}
                    Pré-mapper avec l'IA
                  </Button>
                )}
              </div>

              {rows.length === 0 ? (
                <div className="rounded border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                  Aucun champ personnalisé V1 actif pour ce formulaire.
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '32%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '34%' }} />
                      <col style={{ width: 'auto' }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200 text-xs">
                          Libellé V1
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200 text-xs">
                          Type V1
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200 text-xs">
                          Cible V2
                        </th>
                        <th className="px-3 py-2.5 text-left font-medium text-gray-700 border-b border-gray-200 text-xs">
                          Ce qui va se passer
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const editState = edits[r.sourceId];
                        const v = verdictFor(r);
                        return (
                          <tr key={r.sourceId} className="border-b border-gray-100 last:border-0">
                            <td className="px-3 py-2 align-top">
                              <div className="font-medium text-gray-900 truncate" title={r.sourceLabel}>
                                {r.sourceLabel || <em className="text-gray-400">(sans libellé)</em>}
                              </div>
                              {r.sourceRequired && (
                                <Badge variant="outline" className="mt-1 text-xs">requis V1</Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top text-gray-600">
                              <code className="text-xs">{r.sourceType}</code>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <Select value={selectValue(r)} onValueChange={(val) => onSelect(r, val)}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Choisir…" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NONE_VALUE}>
                                    <em>— Non mappé —</em>
                                  </SelectItem>
                                  {v2Attributes.length > 0 && (
                                    <SelectGroup>
                                      <SelectLabel>Réutiliser un champ V2 existant</SelectLabel>
                                      {v2Attributes.map((a) => (
                                        <SelectItem key={a.id} value={`attr:${a.id}`}>
                                          {a.name}
                                          <span className="text-xs text-gray-400"> ({a.type ?? '—'})</span>
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  )}
                                  <SelectGroup>
                                    <SelectLabel>Créer un nouveau champ (type)</SelectLabel>
                                    {V2_ATTRIBUTE_TYPES.map((t) => (
                                      <SelectItem key={t.code} value={`type:${t.code}`}>
                                        {t.label} <span className="text-xs text-gray-400">({t.code})</span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {v.kind === 'existing' && (
                                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs">
                                    Champ existant{v.name ? ` ↦ ${v.name}` : ''}
                                  </Badge>
                                )}
                                {v.kind === 'reuse' && (
                                  <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-xs">
                                    Réutilisé (auto){v.name ? ` ↦ ${v.name}` : ''}
                                  </Badge>
                                )}
                                {v.kind === 'create' && (
                                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                                    Sera créé
                                  </Badge>
                                )}
                                {v.kind === 'none' && (
                                  <Badge variant="destructive" className="text-xs">À mapper</Badge>
                                )}
                                {editState?.source === 'ai' && (
                                  <span
                                    className="inline-flex items-center text-violet-500"
                                    title={`Suggestion IA${
                                      editState.confidence != null
                                        ? ` · confiance ${Math.round(editState.confidence * 100)}%`
                                        : ''
                                    }`}
                                  >
                                    <Sparkles className="h-3.5 w-3.5" />
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </SheetBody>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={savingAll}>
            Fermer
          </Button>
          <Button onClick={saveAll} disabled={dirtyRows.length === 0 || savingAll}>
            {savingAll ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Enregistrer{dirtyRows.length > 0 ? ` (${dirtyRows.length})` : ''}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
